import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "../components/ui/toggle-group";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../components/ui/chart";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

// Define types for shareholding data
type Shareholding = {
  shareholders: string;
  year: string;
  financial_year: string;
  category: string;
  indian_held_no_of_shares: number;
  indian_held_percentage_of_shares: number | null;
  nri_held_no_of_shares: number;
  nri_held_percentage_of_shares: number | null;
  foreign_held_other_than_nri_no_of_shares: number;
  foreign_held_other_than_nri_percentage_of_shares: number | null;
  central_government_held_no_of_shares: number;
  central_government_held_percentage_of_shares: number | null;
  state_government_held_no_of_shares: number;
  state_government_held_percentage_of_shares: number | null;
  government_company_held_no_shares: number;
  government_company_held_percentage_of_shares: number | null;
  insurance_company_held_no_of_shares: number;
  insurance_company_held_percentage_of_shares: number | null;
  bank_held_no_of_shares: number;
  bank_held_percentage_of_shares: number | null;
  financial_institutions_held_no_of_shares: number;
  financial_institutions_held_percentage_of_shares: number | null;
  financial_institutions_investors_held_no_of_shares: number;
  financial_institutions_investors_held_percentage_of_shares: number | null;
  mutual_funds_held_no_of_shares: number;
  mutual_funds_held_percentage_of_shares: number | null;
  venture_capital_held_no_of_shares: number;
  venture_capital_held_percentage_of_shares: number | null;
  body_corporate_held_no_of_shares: number;
  body_corporate_held_percentage_of_shares: number | null;
  others_held_no_of_shares: number;
  others_held_percentage_of_shares: number | null;
  total_no_of_shares: number;
  total_percentage_of_shares: number | null;
};

type DirectorShareholding = {
  year: string;
  financial_year: string;
  din_pan: string;
  full_name: string;
  designation: string;
  date_of_cessation: string | null;
  no_of_shares: number;
  percentage_holding: number;
};

type ShareholdingMoreThanFivePercent = {
  financial_year: string;
  company: Array<{
    name: string;
    shareholding_percentage: number;
    no_of_shares: number;
  }>;
  llp: Array<{
    name: string;
    shareholding_percentage: number;
    no_of_shares: number;
  }>;
  individual: Array<{
    name: string;
    shareholding_percentage: number;
    no_of_shares: number;
  }>;
  others: Array<{
    name: string;
    shareholding_percentage: number;
    no_of_shares: number;
  }>;
};

interface ShareholdingPatternProps {
  shareholdings: Shareholding[];
  directorShareholdings: DirectorShareholding[];
  shareholdingsMoreThanFivePercent?: ShareholdingMoreThanFivePercent[];
}

export default function ShareholdingPattern({
  shareholdings,
  directorShareholdings,
  shareholdingsMoreThanFivePercent = [],
}: ShareholdingPatternProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>("equity");

  // Filter shareholdings based on selected category
  const filteredShareholdings = shareholdings.filter(
    (holding) => holding.category === selectedCategory
  );

  // Calculate total shareholding percentage for a shareholder type (promoter or public)
  const calculateShareholderTotal = (shareholderType: string) => {
    const data = filteredShareholdings.find(
      (holding) => holding.shareholders === shareholderType
    );
    return data ? data.total_percentage_of_shares || 0 : 0;
  };

  // Format percentage values for display
  const formatPercentage = (value: number | null, shares: number | null) => {
    if (value === null || value === undefined) return "-";
    if (value === 0 && shares && shares > 0) return "< 0.01%";
    if (value === 0) return "-";
    return `${value}%`;
  };

  // Check if there's any data for the selected category
  const hasData = filteredShareholdings.some(
    (holding) => holding.total_no_of_shares > 0
  );

  // Calculate shareholdings for promoters
  const promoterData = filteredShareholdings.find(
    (h) => h.shareholders === "promoter"
  );

  // Individual/HUF calculation (combined)
  const individualHufShares =
    (promoterData?.indian_held_no_of_shares || 0) +
    (promoterData?.nri_held_no_of_shares || 0) +
    (promoterData?.foreign_held_other_than_nri_no_of_shares || 0);

  const individualHufPercentage =
    (promoterData?.indian_held_percentage_of_shares || 0) +
    (promoterData?.nri_held_percentage_of_shares || 0) +
    (promoterData?.foreign_held_other_than_nri_percentage_of_shares || 0);

  // Body Corporate for promoters
  const promoterBodyCorpShares =
    promoterData?.body_corporate_held_no_of_shares || 0;
  const promoterBodyCorpPercentage =
    promoterData?.body_corporate_held_percentage_of_shares || 0;

  // Calculate shareholdings for public
  const publicData = filteredShareholdings.find(
    (h) => h.shareholders === "public"
  );

  // Mapping for public shareholding categories
  const publicShareholdingMap = [
    {
      name: "Government",
      shares:
        (publicData?.central_government_held_no_of_shares || 0) +
        (publicData?.state_government_held_no_of_shares || 0) +
        (publicData?.government_company_held_no_shares || 0),
      percentage:
        (publicData?.central_government_held_percentage_of_shares || 0) +
        (publicData?.state_government_held_percentage_of_shares || 0) +
        (publicData?.government_company_held_percentage_of_shares || 0),
    },
    {
      name: "Insurance Companies",
      shares: publicData?.insurance_company_held_no_of_shares || 0,
      percentage: publicData?.insurance_company_held_percentage_of_shares || 0,
    },
    {
      name: "Banks",
      shares: publicData?.bank_held_no_of_shares || 0,
      percentage: publicData?.bank_held_percentage_of_shares || 0,
    },
    {
      name: "Financial Institutions",
      shares: publicData?.financial_institutions_held_no_of_shares || 0,
      percentage:
        publicData?.financial_institutions_held_percentage_of_shares || 0,
    },
    {
      name: "Foreign Institutional Investors",
      shares:
        publicData?.financial_institutions_investors_held_no_of_shares || 0,
      percentage:
        publicData?.financial_institutions_investors_held_percentage_of_shares ||
        0,
    },
    {
      name: "Mutual Funds",
      shares: publicData?.mutual_funds_held_no_of_shares || 0,
      percentage: publicData?.mutual_funds_held_percentage_of_shares || 0,
    },
    {
      name: "Venture Capital",
      shares: publicData?.venture_capital_held_no_of_shares || 0,
      percentage: publicData?.venture_capital_held_percentage_of_shares || 0,
    },
    {
      name: "Body Corporate",
      shares: publicData?.body_corporate_held_no_of_shares || 0,
      percentage: publicData?.body_corporate_held_percentage_of_shares || 0,
    },
    {
      name: "Others",
      shares: publicData?.others_held_no_of_shares || 0,
      percentage: publicData?.others_held_percentage_of_shares || 0,
    },
  ];

  // Filter out any categories with zero shares (unless they have > 0 percentage)
  const filteredPromoterCategories = [
    {
      name: "Individual / HUF",
      shares: individualHufShares,
      percentage: individualHufPercentage,
    },
    {
      name: "Body Corporate",
      shares: promoterBodyCorpShares,
      percentage: promoterBodyCorpPercentage,
    },
  ].filter((item) => item.shares > 0 || (item.percentage || 0) > 0);

  const filteredPublicCategories = publicShareholdingMap.filter(
    (item) => item.shares > 0 || (item.percentage || 0) > 0
  );

  // Prepare chart data for pie chart
  const chartData = React.useMemo(() => {
    const promoterData = filteredShareholdings.find(
      (h) => h.shareholders === "promoter"
    );
    const publicData = filteredShareholdings.find(
      (h) => h.shareholders === "public"
    );

    const data = [];

    if (promoterData && (promoterData.total_percentage_of_shares || 0) > 0) {
      data.push({
        name: "Promoter",
        value: promoterData.total_percentage_of_shares || 0,
        fill: "#3b82f6",
      });
    }

    if (publicData && (publicData.total_percentage_of_shares || 0) > 0) {
      data.push({
        name: "Public",
        value: publicData.total_percentage_of_shares || 0,
        fill: "#06b6d4",
      });
    }

    return data;
  }, [filteredShareholdings]);

  // Prepare detailed chart data for bar chart
  const detailedChartData = React.useMemo(() => {
    const data: any = [];

    // Add promoter categories
    if (filteredPromoterCategories.length > 0) {
      filteredPromoterCategories.forEach((item) => {
        if ((item.percentage || 0) > 0) {
          data.push({
            name: item.name,
            percentage: item.percentage || 0,
            type: "Promoter",
            fill: "#3b82f6",
          });
        }
      });
    }

    // Add public categories (top 5 only for better visualization)
    if (filteredPublicCategories.length > 0) {
      filteredPublicCategories
        .filter((item) => (item.percentage || 0) > 0)
        .sort((a, b) => (b.percentage || 0) - (a.percentage || 0))
        .slice(0, 5)
        .forEach((item) => {
          data.push({
            name: item.name,
            percentage: item.percentage || 0,
            type: "Public",
            fill: "#06b6d4",
          });
        });
    }

    return data;
  }, [filteredPromoterCategories, filteredPublicCategories]);

  const chartConfig = {
    promoter: {
      label: "Promoter",
      color: "#3b82f6",
    },
    public: {
      label: "Public",
      color: "#06b6d4",
    },
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-bold">Shareholding Pattern</h2>
        <p className="text-gray-600">
          View equity and preference shareholding details
        </p>
      </div>

      {/* Toggle between Equity and Preference */}
      <div>
        <ToggleGroup
          type="single"
          value={selectedCategory}
          onValueChange={(value) => value && setSelectedCategory(value)}
          className="justify-start"
        >
          <ToggleGroupItem value="equity" className="px-4 py-2">
            Equity
          </ToggleGroupItem>
          <ToggleGroupItem value="preference" className="px-4 py-2">
            Preference
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {/* Shareholding Pattern Content */}
      {hasData ? (
        <div className="space-y-8">
          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pie Chart - Overall Distribution */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">
                Overall Distribution
              </h3>
              {chartData.length > 0 ? (
                <ChartContainer config={chartConfig} className="h-[300px]">
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                    style={{ marginLeft: "-50px" }}
                  >
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${value}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <ChartTooltip content={<ChartTooltipContent />} />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-500">
                  No data available for chart
                </div>
              )}
            </div>

            {/* Bar Chart - Detailed Breakdown */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Detailed Breakdown</h3>
              {detailedChartData.length > 0 ? (
                <ChartContainer config={chartConfig} className="h-[300px]">
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                    style={{ marginLeft: "-50px" }}
                  >
                    <BarChart
                      data={detailedChartData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        fontSize={10}
                      />
                      <YAxis />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="percentage" fill="#3b82f6" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-500">
                  No data available for chart
                </div>
              )}
            </div>
          </div>

          {/* Shareholding Pattern Table */}
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold">Name</TableHead>
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold">
                    Shareholding (%)
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Promoter Section */}
                {filteredPromoterCategories.length > 0 && (
                  <>
                    {filteredPromoterCategories.map((item, index) => (
                      <TableRow
                        key={`promoter-${index}`}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <TableCell className="font-medium">
                          {item.name}
                        </TableCell>
                        <TableCell>Promoter</TableCell>
                        <TableCell>
                          {formatPercentage(item.percentage, item.shares)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}

                {/* Public Section */}
                {filteredPublicCategories.length > 0 && (
                  <>
                    {filteredPublicCategories.map((item, index) => (
                      <TableRow
                        key={`public-${index}`}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <TableCell className="font-medium">
                          {item.name}
                        </TableCell>
                        <TableCell>Non-Promoter</TableCell>
                        <TableCell>
                          {formatPercentage(item.percentage, item.shares)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}

                {/* Summary Row */}
                <TableRow className="hover:bg-gray-50 transition-colors">
                  <TableCell>Total</TableCell>
                  <TableCell>All</TableCell>
                  <TableCell>
                    {formatPercentage(
                      calculateShareholderTotal("promoter") +
                        calculateShareholderTotal("public"),
                      1
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          {/* Directors' Shareholding Section */}
          {directorShareholdings.length > 0 && (
            <div className="space-y-4 ">
              <h3 className="text-lg font-semibold">Directors' Shareholding</h3>
              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Name</TableHead>
                      <TableHead className="font-semibold">
                        Designation
                      </TableHead>
                      <TableHead className="font-semibold">
                        Shareholding (%)
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {directorShareholdings
                      .filter(
                        (dir) =>
                          dir.no_of_shares > 0 || dir.percentage_holding > 0
                      )
                      .map((director, index) => (
                        <TableRow
                          key={`director-${index}`}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <TableCell className="font-medium">
                            {director.full_name}
                          </TableCell>
                          <TableCell>{director.designation}</TableCell>
                          <TableCell>
                            {formatPercentage(
                              director.percentage_holding,
                              director.no_of_shares
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Shareholding more than 5%</h3>

            {shareholdingsMoreThanFivePercent.length &&
            shareholdingsMoreThanFivePercent.every(
              (y) =>
                y.company.length === 0 &&
                y.llp.length === 0 &&
                y.individual.length === 0 &&
                y.others.length === 0
            ) ? (
              <div className="py-12 text-center text-gray-500">
                No shareholding above 5% found for this company.
              </div>
            ) : (
              shareholdingsMoreThanFivePercent.map((yearData, yearIndex) => {
                const hasData =
                  yearData.company.length ||
                  yearData.llp.length ||
                  yearData.individual.length ||
                  yearData.others.length;

                if (!hasData) return null;

                return (
                  <div key={yearIndex} className="space-y-4">
                    <h4 className="text-md font-medium text-gray-700">
                      Financial Year: {yearData.financial_year}
                    </h4>
                    <div className="rounded-lg border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="font-semibold">
                              Name
                            </TableHead>
                            <TableHead className="font-semibold">
                              Type
                            </TableHead>
                            <TableHead className="font-semibold">
                              Shareholding (%)
                            </TableHead>
                            <TableHead className="font-semibold">
                              No. of Shares
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {yearData.company.map((shareholder, index) => (
                            <TableRow
                              key={`company-${index}`}
                              className="hover:bg-gray-50 transition-colors"
                            >
                              <TableCell className="font-medium">
                                {shareholder.name}
                              </TableCell>
                              <TableCell>Company</TableCell>
                              <TableCell>
                                {shareholder.shareholding_percentage}%
                              </TableCell>
                              <TableCell>
                                {shareholder?.no_of_shares?.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                          {yearData.llp.map((shareholder, index) => (
                            <TableRow
                              key={`llp-${index}`}
                              className="hover:bg-gray-50 transition-colors"
                            >
                              <TableCell className="font-medium">
                                {shareholder.name}
                              </TableCell>
                              <TableCell>LLP</TableCell>
                              <TableCell>
                                {shareholder.shareholding_percentage}%
                              </TableCell>
                              <TableCell>
                                {shareholder?.no_of_shares?.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                          {yearData.individual.map((shareholder, index) => (
                            <TableRow
                              key={`individual-${index}`}
                              className="hover:bg-gray-50 transition-colors"
                            >
                              <TableCell className="font-medium">
                                {shareholder.name}
                              </TableCell>
                              <TableCell>Individual</TableCell>
                              <TableCell>
                                {shareholder.shareholding_percentage}%
                              </TableCell>
                              <TableCell>
                                {shareholder?.no_of_shares?.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                          {yearData.others.map((shareholder, index) => (
                            <TableRow
                              key={`others-${index}`}
                              className="hover:bg-gray-50 transition-colors"
                            >
                              <TableCell className="font-medium">
                                {shareholder.name}
                              </TableCell>
                              <TableCell>Others</TableCell>
                              <TableCell>
                                {shareholder.shareholding_percentage}%
                              </TableCell>
                              <TableCell>
                                {shareholder?.no_of_shares?.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      ) : (
        <div className="py-12 text-center text-gray-500">
          No data available for{" "}
          {selectedCategory === "equity" ? "Equity" : "Preference"} Shareholding
        </div>
      )}
    </div>
  );
}
